/* eslint-disable max-len */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { DataTable } from 'primereact/datatable';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { injectIntl, defineMessages } from 'react-intl';
import ArrayUtil from '../../../utils/ArrayUtil';
import MemberListHeader from './MemberListHeader';
import { connectionShape, intlShape } from '../../../components/prop-types';
import { getUserLanguage } from '../../../utils/LanguageUtil';
import Paginator from './MemberListPaginator';

const messages = defineMessages({
  name: { id: 'attrs.name', defaultMessage: 'Name' },
  template: { id: 'assessment-template', defaultMessage: 'Assessment Template' },
  language: { id: 'attrs.language', defaultMessage: 'Language' },
  status: { id: 'attrs.status', defaultMessage: 'Status' },
  createdOn: { id: 'exec_report_detail.survey_created-on', defaultMessage: 'Created on' },
  targetJobProfile: { id: 'attrs.targetJob', defaultMessage: 'Target Job Profile' },
});

const nameTemplate = (rowData, { currentAccount }) => {
  const detailUrl = `/accounts/${currentAccount}/respondents/${rowData.respondent.id}`;

  const fullName = `${rowData.respondent.firstName} ${rowData.respondent.lastName}`;

  return (
    <Link to={detailUrl} className="app-link">
      {fullName}
    </Link>
  );
};
const assessmentTemplateName = rowData => <span>{rowData.template}</span>;
const languageTemplate = (rowData, col) => {
  const language = getUserLanguage(rowData.locale, col.translatedTags);
  return language.label;
};
const targetJobProfileName = rowData => <span>{rowData.targetJobProfile}</span>;

const selectManagerRow = (assessment, { selectSpecTeam, selectedTeams }) => (
  <div>
    <Checkbox
      type="checkbox"
      id="selectAllManagers"
      onChange={() => {
        selectSpecTeam(assessment.id);
      }}
      checked={ArrayUtil.isNotEmpty(selectedTeams) && selectedTeams.indexOf(assessment.id) >= 0}
    />
  </div>
);

const HeaderManagers = (props) => {
  const { addAllTeams, isAllChecked, edges } = props;
  const allChecked = isAllChecked(edges);
  return (
    <div>
      <Checkbox
        type="checkbox"
        id="selectAllManagers"
        onChange={() => {
          addAllTeams(allChecked, edges);
        }}
        checked={allChecked}
      />
    </div>
  );
};

HeaderManagers.propTypes = {
  addAllTeams: PropTypes.func.isRequired,
  isAllChecked: PropTypes.func.isRequired,
  edges: PropTypes.arrayOf(PropTypes.any).isRequired,
};

const timeFieldTemplate = (rowData, { formatDate, field }) => {
  const updated = formatDate(moment(rowData[field]), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return updated;
};

class MembersList extends Component {
  render() {
    const {
      connection: {
        edges, totalCount, indexFrom, indexTo, pageInfo,
      },
      intl: { formatMessage, formatDate },
      currentAccount,
      loading,
      translatedTags,
      selectSpecTeam,
      selectedTeams,
      addAllTeams,
      isAllChecked,
      onQueryChange,
      query,
      onTempChange,
      search,
      changePageSize,
      movePrev,
      moveNext,
    } = this.props;

    // data list
    const listRows = edges.map(({ node }) => ({
      ...node,
      template: node.template ? node.template.title : '',
      createdOn: node.createdOn,
      targetJobProfile: node.respondent.targetJob ? node.respondent.targetJob.name : '---',
    }));

    // header
    const headerRespondentName = formatMessage(messages.name);
    const headerTemplate = formatMessage(messages.template);
    const headerLanguage = formatMessage(messages.language);
    const headerStatus = formatMessage(messages.status);
    const headerCreatedOn = formatMessage(messages.createdOn);
    const headerTargetJobProfile = formatMessage(messages.targetJobProfile);

    const headerManagerTemplate = (
      <HeaderManagers
        addAllTeams={addAllTeams}
        isAllChecked={isAllChecked}
        edges={listRows}
      />
    );

    // footer
    const footer = (
      <Paginator
        totalCount={totalCount}
        indexFrom={indexFrom}
        indexTo={indexTo}
        pageInfo={pageInfo}
        search={search}
        changePageSize={changePageSize}
        movePrev={movePrev}
        moveNext={moveNext}
      />
    );

    return (
      <div className="p-grid">
        <div className="p-col-12">
          <div className="brand-detailed-wrapper">
            <div className="p-grid">
              <div className="p-col-12">
                <MemberListHeader
                  onQueryChange={onQueryChange}
                  onTempChange={onTempChange}
                  query={query}
                />
              </div>
              <div className="p-col-12">
                <DataTable
                  value={listRows}
                  responsive
                  lazy
                  footer={footer}
                  loading={loading}
                >
                  <Column
                    style={{ textAlign: 'center', width: '3em' }}
                    field="id"
                    header={headerManagerTemplate}
                    body={selectManagerRow}
                    selectSpecTeam={selectSpecTeam}
                    selectedTeams={selectedTeams}
                  />
                  <Column
                    field="title"
                    header={headerRespondentName}
                    currentAccount={currentAccount}
                    body={nameTemplate}
                  />
                  <Column field="template" header={headerTemplate} body={assessmentTemplateName} />
                  <Column field="locale" header={headerLanguage} translatedTags={translatedTags} body={languageTemplate} />
                  <Column field="status" header={headerStatus} />
                  <Column
                    field="createdOn"
                    header={headerCreatedOn}
                    body={timeFieldTemplate}
                    formatDate={formatDate}
                  />
                  <Column
                    field="targetJobProfile"
                    header={headerTargetJobProfile}
                    currentAccount={currentAccount}
                    body={targetJobProfileName}
                  />
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

MembersList.propTypes = {
  connection: connectionShape.isRequired,
  intl: intlShape.isRequired,
  currentAccount: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  selectSpecTeam: PropTypes.func.isRequired,
  selectedTeams: PropTypes.arrayOf(PropTypes.any),
  addAllTeams: PropTypes.func.isRequired,
  isAllChecked: PropTypes.func.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired,
  onTempChange: PropTypes.func.isRequired,
  search: PropTypes.objectOf(PropTypes.any).isRequired,
  changePageSize: PropTypes.func.isRequired,
  movePrev: PropTypes.func.isRequired,
  moveNext: PropTypes.func.isRequired,
  translatedTags: PropTypes.arrayOf(PropTypes.any).isRequired,
};

MembersList.defaultProps = {
  selectedTeams: [],
};

export default injectIntl(MembersList);
